import {
  createElement,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import {
  Check,
  Loader2,
  MessageSquarePlus,
  PanelRightClose,
  PanelRightOpen,
  Trash2,
  X,
} from "lucide-react"
import Markdown, { defaultUrlTransform, type Components } from "react-markdown"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import {
  getEmbeddedDataImagePreview,
  isEmbeddedDataImageUrl,
} from "@f2e/shared/utils"

import {
  type ContextObjFAQTextRangeSchema,
  type KnowledgeBaseContextObjFAQSchema,
  useCreateKnowledgeBaseNodeFaqApi,
  useDeleteKnowledgeBaseNodeFaqApi,
  useListKnowledgeBaseNodeFaqsApi,
  useUpdateKnowledgeBaseNodeFaqApi,
} from "@/hooks/api"
import { useToast } from "@/hooks"

const FAQ_QUESTION_MAX_LENGTH = 100
const HIGHLIGHT_NAME = "kb-faq-text-ranges"
const DRAFT_HIGHLIGHT_NAME = "kb-faq-draft-text-range"
const EDIT_HIGHLIGHT_NAME = "kb-faq-edit-text-range"
const FAQ_ANCHOR_GROUP_THRESHOLD_PX = 24
const FAQ_TEXT_PREVIEW_MAX_LENGTH = 220
const FAQ_PANEL_WIDE_MEDIA_QUERY = "(min-width: 80rem)"
const EMPTY_FAQS: KnowledgeBaseContextObjFAQSchema[] = []

const useIsFaqPanelStacked = () => {
  const [isWide, setIsWide] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(FAQ_PANEL_WIDE_MEDIA_QUERY).matches
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia(FAQ_PANEL_WIDE_MEDIA_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      setIsWide(event.matches)
    }

    setIsWide(mediaQuery.matches)
    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [])

  return !isWide
}

interface SourcePosition {
  start?: { offset?: number | null }
  end?: { offset?: number | null }
}

interface MarkdownNodeWithPosition {
  position?: SourcePosition
}

interface KnowledgeBaseTextRangeFaqViewerProps {
  content: string
  organizationId: string
  workspaceId: string
  kbId: string
  nodeId: string
}

interface PendingSelection {
  range: ContextObjFAQTextRangeSchema
  top: number
}

interface FaqAnchor {
  faq: KnowledgeBaseContextObjFAQSchema
  top: number
}

interface FaqAnchorGroup {
  id: string
  top: number
  anchors: FaqAnchor[]
}

interface ActiveEditor {
  mode: "create" | "edit"
  faq?: KnowledgeBaseContextObjFAQSchema
  range: ContextObjFAQTextRangeSchema
  top: number
}

interface OrderedFaq {
  faq: KnowledgeBaseContextObjFAQSchema
  index: number
}

interface SourceMappedBlockProps {
  as: keyof React.JSX.IntrinsicElements
  node?: MarkdownNodeWithPosition
  children?: ReactNode
  className?: string
}

type CssHighlightRegistry = Map<string, Highlight>

const markdownUrlTransform = (url: string) =>
  isEmbeddedDataImageUrl(url) ? url : defaultUrlTransform(url)

const getNodeSourceOffsets = (node?: MarkdownNodeWithPosition) => {
  const startOffset = node?.position?.start?.offset
  const endOffset = node?.position?.end?.offset
  if (typeof startOffset !== "number" || typeof endOffset !== "number") {
    return null
  }
  return { startOffset, endOffset }
}

const SourceMappedBlock = ({
  as,
  node,
  children,
  className,
  ...props
}: SourceMappedBlockProps & Record<string, unknown>) => {
  const offsets = getNodeSourceOffsets(node)
  return createElement(
    as,
    {
      ...props,
      className,
      "data-kb-source-start": offsets?.startOffset,
      "data-kb-source-end": offsets?.endOffset,
    },
    children
  )
}

const lineStartOffsets = (content: string) => {
  const starts = [0]
  for (let index = 0; index < content.length; index += 1) {
    if (content[index] === "\n") starts.push(index + 1)
  }
  return starts
}

const offsetToLineColumn = (starts: number[], offset: number) => {
  let lineIndex = 0
  for (let index = 0; index < starts.length; index += 1) {
    if (starts[index] > offset) break
    lineIndex = index
  }
  return {
    line: lineIndex + 1,
    column: Math.max(0, offset - starts[lineIndex]),
  }
}

const renderedIndexToSourceOffset = (
  source: string,
  rendered: string,
  renderedIndex: number,
  sourceStartOffset: number,
  preferEnd = false
) => {
  if (renderedIndex <= 0) return sourceStartOffset
  const sourceOffsets: number[] = []
  let sourceIndex = 0

  for (
    let renderedIndexCursor = 0;
    renderedIndexCursor < rendered.length;
    renderedIndexCursor += 1
  ) {
    const renderedChar = rendered[renderedIndexCursor]
    while (
      sourceIndex < source.length &&
      source[sourceIndex] !== renderedChar
    ) {
      sourceIndex += 1
    }
    if (sourceIndex >= source.length) break
    sourceOffsets.push(sourceStartOffset + sourceIndex)
    sourceIndex += 1
  }

  const mapped = sourceOffsets[renderedIndex - 1]
  if (typeof mapped === "number") return preferEnd ? mapped + 1 : mapped
  return sourceStartOffset + Math.min(source.length, renderedIndex)
}

const sourceOffsetToRenderedIndex = (
  source: string,
  rendered: string,
  targetSourceOffset: number,
  sourceStartOffset: number
) => {
  let sourceIndex = 0
  for (
    let renderedIndex = 0;
    renderedIndex < rendered.length;
    renderedIndex += 1
  ) {
    const renderedChar = rendered[renderedIndex]
    while (
      sourceIndex < source.length &&
      source[sourceIndex] !== renderedChar
    ) {
      sourceIndex += 1
    }
    if (sourceIndex >= source.length) break
    if (sourceStartOffset + sourceIndex >= targetSourceOffset)
      return renderedIndex
    sourceIndex += 1
  }
  return rendered.length
}

const textOffsetInsideElement = (
  element: HTMLElement,
  node: Node,
  offset: number
) => {
  const range = document.createRange()
  range.selectNodeContents(element)
  range.setEnd(node, offset)
  return range.toString().length
}

const domPointFromTextOffset = (element: HTMLElement, offset: number) => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  let remaining = offset
  let current = walker.nextNode()
  while (current) {
    const length = current.textContent?.length ?? 0
    if (remaining <= length) return { node: current, offset: remaining }
    remaining -= length
    current = walker.nextNode()
  }
  return { node: element, offset: element.childNodes.length }
}

const closestSourceBlock = (node: Node | null) => {
  const element =
    node instanceof HTMLElement ? node : (node?.parentElement ?? null)
  return element?.closest<HTMLElement>("[data-kb-source-start]") ?? null
}

const blockSourceOffsets = (element: HTMLElement) => {
  const startOffset = Number(element.dataset.kbSourceStart)
  const endOffset = Number(element.dataset.kbSourceEnd)
  if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset)) return null
  return { startOffset, endOffset }
}

const findBlockForSourceOffset = (blocks: HTMLElement[], offset: number) =>
  blocks.find((block) => {
    const offsets = blockSourceOffsets(block)
    return (
      !!offsets && offsets.startOffset <= offset && offsets.endOffset >= offset
    )
  }) ?? null

const buildTextRangeFromSelection = (
  selection: Selection,
  content: string,
  starts: number[]
): ContextObjFAQTextRangeSchema | null => {
  if (!selection.anchorNode || !selection.focusNode || selection.isCollapsed) {
    return null
  }

  const anchorBlock = closestSourceBlock(selection.anchorNode)
  const focusBlock = closestSourceBlock(selection.focusNode)
  if (!anchorBlock || !focusBlock) return null

  const anchorOffsets = blockSourceOffsets(anchorBlock)
  const focusOffsets = blockSourceOffsets(focusBlock)
  if (!anchorOffsets || !focusOffsets) return null

  const anchorRenderedOffset = textOffsetInsideElement(
    anchorBlock,
    selection.anchorNode,
    selection.anchorOffset
  )
  const focusRenderedOffset = textOffsetInsideElement(
    focusBlock,
    selection.focusNode,
    selection.focusOffset
  )

  const anchorSource = content.slice(
    anchorOffsets.startOffset,
    anchorOffsets.endOffset
  )
  const focusSource = content.slice(
    focusOffsets.startOffset,
    focusOffsets.endOffset
  )

  const anchorSourceOffset = renderedIndexToSourceOffset(
    anchorSource,
    anchorBlock.textContent ?? "",
    anchorRenderedOffset,
    anchorOffsets.startOffset
  )
  const focusSourceOffset = renderedIndexToSourceOffset(
    focusSource,
    focusBlock.textContent ?? "",
    focusRenderedOffset,
    focusOffsets.startOffset,
    true
  )

  const startOffset = Math.min(anchorSourceOffset, focusSourceOffset)
  const endOffset = Math.max(anchorSourceOffset, focusSourceOffset)
  if (startOffset >= endOffset) return null

  const start = offsetToLineColumn(starts, startOffset)
  const end = offsetToLineColumn(starts, endOffset)
  return {
    start_line: start.line,
    start_column: start.column,
    end_line: end.line,
    end_column: end.column,
    start_offset: startOffset,
    end_offset: endOffset,
    text: selection.toString(),
  }
}

const installFaqHighlights = (
  container: HTMLElement | null,
  content: string,
  faqs: KnowledgeBaseContextObjFAQSchema[],
  draftRange: ContextObjFAQTextRangeSchema | null,
  editRange: ContextObjFAQTextRangeSchema | null
) => {
  const highlightApi = window.CSS as
    | (typeof CSS & { highlights?: CssHighlightRegistry })
    | undefined
  const registry = highlightApi?.highlights
  if (!container || !registry || typeof Highlight === "undefined") return

  registry.delete(HIGHLIGHT_NAME)
  registry.delete(DRAFT_HIGHLIGHT_NAME)
  registry.delete(EDIT_HIGHLIGHT_NAME)
  const ranges: Range[] = []
  const blocks = Array.from(
    container.querySelectorAll<HTMLElement>("[data-kb-source-start]")
  )

  faqs.forEach((faq) => {
    faq.ranges.forEach((faqRange) => {
      const range = domRangeForFaqRange(blocks, content, faqRange)
      if (range) ranges.push(range)
    })
  })

  registry.set(HIGHLIGHT_NAME, new Highlight(...ranges))
  if (draftRange) {
    const range = domRangeForFaqRange(blocks, content, draftRange)
    if (range) registry.set(DRAFT_HIGHLIGHT_NAME, new Highlight(range))
  }
  if (editRange) {
    const range = domRangeForFaqRange(blocks, content, editRange)
    if (range) registry.set(EDIT_HIGHLIGHT_NAME, new Highlight(range))
  }
}

const domRangeForFaqRange = (
  blocks: HTMLElement[],
  content: string,
  faqRange: ContextObjFAQTextRangeSchema
) => {
  if (
    typeof faqRange.start_offset !== "number" ||
    typeof faqRange.end_offset !== "number"
  ) {
    return null
  }
  const startBlock = findBlockForSourceOffset(blocks, faqRange.start_offset)
  const endBlock = findBlockForSourceOffset(blocks, faqRange.end_offset)
  if (!startBlock || !endBlock) return null
  const startOffsets = blockSourceOffsets(startBlock)
  const endOffsets = blockSourceOffsets(endBlock)
  if (!startOffsets || !endOffsets) return null

  const startRenderedOffset = sourceOffsetToRenderedIndex(
    content.slice(startOffsets.startOffset, startOffsets.endOffset),
    startBlock.textContent ?? "",
    faqRange.start_offset,
    startOffsets.startOffset
  )
  const endRenderedOffset = sourceOffsetToRenderedIndex(
    content.slice(endOffsets.startOffset, endOffsets.endOffset),
    endBlock.textContent ?? "",
    faqRange.end_offset,
    endOffsets.startOffset
  )
  const startPoint = domPointFromTextOffset(startBlock, startRenderedOffset)
  const endPoint = domPointFromTextOffset(endBlock, endRenderedOffset)
  const range = document.createRange()
  range.setStart(startPoint.node, startPoint.offset)
  range.setEnd(endPoint.node, endPoint.offset)
  return range
}

const faqAnchorRect = (
  container: HTMLElement | null,
  content: string,
  faq: KnowledgeBaseContextObjFAQSchema
) => {
  if (!container) return null
  const faqRange = faq.ranges[0]
  if (!faqRange) return null
  const blocks = Array.from(
    container.querySelectorAll<HTMLElement>("[data-kb-source-start]")
  )
  const range = domRangeForFaqRange(blocks, content, faqRange)
  if (!range) return null
  const rects = Array.from(range.getClientRects())
  return rects[0] ?? range.getBoundingClientRect()
}

const rectTopInRoot = (root: HTMLElement | null, rect: DOMRect) => {
  if (!root) return rect.top
  return rect.top - root.getBoundingClientRect().top
}

const groupFaqAnchors = (anchors: FaqAnchor[]): FaqAnchorGroup[] => {
  const groups: FaqAnchorGroup[] = []
  const sortedAnchors = [...anchors].sort(
    (first, second) => first.top - second.top
  )

  sortedAnchors.forEach((anchor) => {
    const group = groups.find(
      (item) => Math.abs(item.top - anchor.top) <= FAQ_ANCHOR_GROUP_THRESHOLD_PX
    )
    if (group) {
      group.anchors.push(anchor)
      group.top =
        group.anchors.reduce((sum, item) => sum + item.top, 0) /
        group.anchors.length
      group.id = group.anchors.map((item) => item.faq.id).join("-")
      return
    }
    groups.push({
      id: String(anchor.faq.id),
      top: anchor.top,
      anchors: [anchor],
    })
  })

  return groups
}

const faqStartOffset = (faq: KnowledgeBaseContextObjFAQSchema) => {
  const startOffset = faq.ranges[0]?.start_offset
  return typeof startOffset === "number" ? startOffset : Number.MAX_SAFE_INTEGER
}

const getFaqRangeText = (faq?: KnowledgeBaseContextObjFAQSchema) =>
  faq?.ranges[0]?.text?.trim() ?? ""

const truncatePreviewText = (text: string) => {
  const normalized = text.replace(/\s+/g, " ").trim()
  if (normalized.length <= FAQ_TEXT_PREVIEW_MAX_LENGTH) return normalized
  return `${normalized.slice(0, FAQ_TEXT_PREVIEW_MAX_LENGTH - 1).trimEnd()}…`
}

export function KnowledgeBaseTextRangeFaqViewer({
  content,
  organizationId,
  workspaceId,
  kbId,
  nodeId,
}: KnowledgeBaseTextRangeFaqViewerProps) {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const isFaqPanelStacked = useIsFaqPanelStacked()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const starts = useMemo(() => lineStartOffsets(content), [content])
  const urlBuilderParams = useMemo(
    () => ({ organizationId, workspaceId, kbId, nodeId }),
    [kbId, nodeId, organizationId, workspaceId]
  )
  const faqRequestKey = `${organizationId}:${workspaceId}:${kbId}:${nodeId}`
  const [faqState, setFaqState] = useState<{
    faqs: KnowledgeBaseContextObjFAQSchema[]
    requestKey: string
  }>({ faqs: [], requestKey: faqRequestKey })
  const [pendingSelection, setPendingSelection] =
    useState<PendingSelection | null>(null)
  const [faqAnchors, setFaqAnchors] = useState<FaqAnchor[]>([])
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  const [activeEditor, setActiveEditor] = useState<ActiveEditor | null>(null)
  const [isFaqPanelOpen, setIsFaqPanelOpen] = useState(true)
  const [deleteCandidate, setDeleteCandidate] =
    useState<KnowledgeBaseContextObjFAQSchema | null>(null)
  const [question, setQuestion] = useState("")

  const { fetchFn: listFaqs, status: listStatus } =
    useListKnowledgeBaseNodeFaqsApi()
  const { fetchFn: createFaq, status: createStatus } =
    useCreateKnowledgeBaseNodeFaqApi()
  const { fetchFn: updateFaq, status: updateStatus } =
    useUpdateKnowledgeBaseNodeFaqApi()
  const { fetchFn: deleteFaq, status: deleteStatus } =
    useDeleteKnowledgeBaseNodeFaqApi()
  const listFaqsRef = useRef(listFaqs)
  const alertToastRef = useRef(alertToast)
  const tRef = useRef(t)
  const isSaving = createStatus === "loading" || updateStatus === "loading"
  const isDeleting = deleteStatus === "loading"
  const faqs =
    faqState.requestKey === faqRequestKey ? faqState.faqs : EMPTY_FAQS
  const setFaqs = useCallback(
    (update: SetStateAction<KnowledgeBaseContextObjFAQSchema[]>) => {
      setFaqState((current) => {
        const currentFaqs =
          current.requestKey === faqRequestKey ? current.faqs : []
        const nextFaqs =
          typeof update === "function" ? update(currentFaqs) : update
        return { faqs: nextFaqs, requestKey: faqRequestKey }
      })
    },
    [faqRequestKey]
  )
  const faqAnchorGroups = useMemo(
    () => groupFaqAnchors(faqAnchors),
    [faqAnchors]
  )
  const activeGroup = useMemo(
    () => faqAnchorGroups.find((group) => group.id === activeGroupId) ?? null,
    [activeGroupId, faqAnchorGroups]
  )
  const orderedFaqs = useMemo<OrderedFaq[]>(
    () =>
      [...faqs]
        .sort((first, second) => {
          const offsetDelta = faqStartOffset(first) - faqStartOffset(second)
          if (offsetDelta !== 0) return offsetDelta
          return first.id - second.id
        })
        .map((faq, index) => ({ faq, index: index + 1 })),
    [faqs]
  )
  const faqIndexById = useMemo(
    () => new Map(orderedFaqs.map(({ faq, index }) => [faq.id, index])),
    [orderedFaqs]
  )

  useEffect(() => {
    listFaqsRef.current = listFaqs
  }, [listFaqs])

  useEffect(() => {
    alertToastRef.current = alertToast
  }, [alertToast])

  useEffect(() => {
    tRef.current = t
  }, [t])

  useEffect(() => {
    let cancelled = false
    listFaqsRef
      .current({ urlBuilderParams, preserveData: true })
      .then((resp) => {
        if (!cancelled) setFaqs(resp?.data ?? [])
      })
      .catch((error) => {
        if (cancelled) return
        console.error("[KB] list text range FAQs failed", error)
        alertToastRef.current({
          title: tRef.current("knowledgeBase.treeView.faqLoadFailed"),
        })
      })
    return () => {
      cancelled = true
    }
  }, [faqRequestKey, setFaqs, urlBuilderParams])

  const draftHighlightRange =
    activeEditor?.mode === "create" ? activeEditor.range : null
  const editHighlightRange =
    activeEditor?.mode === "edit" ? activeEditor.range : null

  useEffect(() => {
    installFaqHighlights(
      containerRef.current,
      content,
      faqs,
      draftHighlightRange,
      editHighlightRange
    )
    return () => {
      const highlightApi = window.CSS as
        | (typeof CSS & { highlights?: CssHighlightRegistry })
        | undefined
      highlightApi?.highlights?.delete(HIGHLIGHT_NAME)
      highlightApi?.highlights?.delete(DRAFT_HIGHLIGHT_NAME)
      highlightApi?.highlights?.delete(EDIT_HIGHLIGHT_NAME)
    }
  }, [content, draftHighlightRange, editHighlightRange, faqs])

  const updateFaqAnchors = useCallback(() => {
    setFaqAnchors(
      faqs
        .map((faq) => {
          const rect = faqAnchorRect(containerRef.current, content, faq)
          return rect
            ? { faq, top: rectTopInRoot(rootRef.current, rect) }
            : null
        })
        .filter((item): item is FaqAnchor => !!item)
    )
  }, [content, faqs])

  useEffect(() => {
    updateFaqAnchors()
    window.addEventListener("resize", updateFaqAnchors)
    window.addEventListener("scroll", updateFaqAnchors, true)
    return () => {
      window.removeEventListener("resize", updateFaqAnchors)
      window.removeEventListener("scroll", updateFaqAnchors, true)
    }
  }, [updateFaqAnchors])

  useLayoutEffect(() => {
    updateFaqAnchors()
  }, [isFaqPanelOpen, updateFaqAnchors])

  useEffect(() => {
    if (activeGroupId && !activeGroup) {
      setActiveGroupId(null)
    }
  }, [activeGroup, activeGroupId])

  const handleMouseUp = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const selection = window.getSelection()
      if (!selection || !containerRef.current) return
      if (
        selection.anchorNode &&
        !containerRef.current.contains(selection.anchorNode)
      ) {
        return
      }
      const textRange = buildTextRangeFromSelection(selection, content, starts)
      if (!textRange) return
      setPendingSelection({
        range: textRange,
        top: rootRef.current
          ? event.clientY - rootRef.current.getBoundingClientRect().top
          : event.clientY,
      })
      setActiveEditor(null)
      setActiveGroupId(null)
    },
    [content, starts]
  )

  useEffect(() => {
    const clearPendingWhenSelectionClears = () => {
      if (activeEditor) return
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) {
        setPendingSelection(null)
      }
    }
    document.addEventListener(
      "selectionchange",
      clearPendingWhenSelectionClears
    )
    return () => {
      document.removeEventListener(
        "selectionchange",
        clearPendingWhenSelectionClears
      )
    }
  }, [activeEditor])

  const openCreateEditor = useCallback(() => {
    if (!pendingSelection) return
    setActiveEditor({
      mode: "create",
      range: pendingSelection.range,
      top: pendingSelection.top,
    })
    setActiveGroupId(null)
    setQuestion("")
  }, [pendingSelection])

  const openEditEditor = useCallback(
    (faq: KnowledgeBaseContextObjFAQSchema, top: number) => {
      const range = faq.ranges[0]
      if (!range) return
      setPendingSelection(null)
      setActiveGroupId(null)
      setActiveEditor({ mode: "edit", faq, range, top })
      setQuestion(faq.question)
    },
    []
  )

  const closeEditor = useCallback(() => {
    if (activeEditor?.mode === "create") {
      setPendingSelection(null)
    }
    setActiveEditor(null)
    setQuestion("")
  }, [activeEditor?.mode])

  const toggleAnchorGroup = useCallback((group: FaqAnchorGroup) => {
    setPendingSelection(null)
    setActiveEditor(null)
    setActiveGroupId((current) => (current === group.id ? null : group.id))
  }, [])

  const saveActiveFaq = useCallback(async () => {
    if (!activeEditor || !question.trim()) return
    try {
      if (activeEditor.mode === "edit" && activeEditor.faq) {
        const resp = await updateFaq({
          urlBuilderParams: { ...urlBuilderParams, faqId: activeEditor.faq.id },
          jsonBody: {
            question: question.trim(),
            ranges: [activeEditor.range],
          },
        })
        const updated = resp?.data
        if (updated) {
          setFaqs((items) =>
            items.map((item) => (item.id === updated.id ? updated : item))
          )
        }
      } else {
        const resp = await createFaq({
          urlBuilderParams,
          jsonBody: {
            question: question.trim(),
            ranges: [activeEditor.range],
          },
        })
        const created = resp?.data
        if (created) setFaqs((items) => [...items, created])
      }
      setIsFaqPanelOpen(true)
      setPendingSelection(null)
      setActiveEditor(null)
      setQuestion("")
      successToast({
        description: t("knowledgeBase.treeView.faqSaved"),
      })
    } catch (error) {
      console.error("[KB] save selected FAQ failed", error)
      alertToast({
        title: t("knowledgeBase.treeView.faqSaveFailed"),
      })
    }
  }, [
    activeEditor,
    alertToast,
    createFaq,
    question,
    setFaqs,
    successToast,
    t,
    updateFaq,
    urlBuilderParams,
  ])

  const requestDeleteActiveFaq = useCallback(() => {
    if (!activeEditor?.faq) return
    setDeleteCandidate(activeEditor.faq)
  }, [activeEditor?.faq])

  const deleteActiveFaq = useCallback(async () => {
    if (!deleteCandidate) return
    const faqId = deleteCandidate.id
    try {
      await deleteFaq({
        urlBuilderParams: { ...urlBuilderParams, faqId },
      })
      setFaqs((items) => items.filter((item) => item.id !== faqId))
      setDeleteCandidate(null)
      setActiveGroupId(null)
      if (activeEditor?.faq?.id === faqId) {
        setActiveEditor(null)
        setQuestion("")
      }
      successToast({
        description: t("knowledgeBase.treeView.faqDeleted"),
      })
    } catch (error) {
      console.error("[KB] delete selected FAQ failed", error)
      alertToast({
        title: t("knowledgeBase.treeView.faqDeleteFailed"),
      })
    }
  }, [
    activeEditor?.faq?.id,
    alertToast,
    deleteCandidate,
    deleteFaq,
    setFaqs,
    successToast,
    t,
    urlBuilderParams,
  ])

  const componentProps = useMemo(
    () => ({
      h1: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="h1"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      h2: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="h2"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      h3: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="h3"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      p: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="p"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      li: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="li"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      blockquote: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="blockquote"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      pre: ({ node, children, className, ...props }) => (
        <SourceMappedBlock
          as="pre"
          node={node as MarkdownNodeWithPosition}
          className={className}
          {...props}
        >
          {children}
        </SourceMappedBlock>
      ),
      img: ({ src = "", alt = "" }) => {
        const isEmbedded = isEmbeddedDataImageUrl(src)
        const srcPreview = isEmbedded ? getEmbeddedDataImagePreview(src) : src
        return (
          <figure className="my-4">
            <img
              src={src}
              alt={alt}
              className="max-h-96 w-auto rounded-md border border-slate-200 dark:border-slate-700"
            />
            <figcaption className="mt-2 break-all text-xs text-slate-500 dark:text-slate-400">
              <code
                title={src}
                className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-900"
              >
                {srcPreview}
              </code>
            </figcaption>
          </figure>
        )
      },
    }),
    []
  ) satisfies Components

  const activeEditorSelectedText = activeEditor
    ? truncatePreviewText(
        activeEditor.range.text?.trim() || getFaqRangeText(activeEditor.faq)
      )
    : ""

  const faqPanel = isFaqPanelOpen ? (
    <aside
      id="knowledge-base-faq-panel"
      className="not-prose flex max-h-[min(72vh,44rem)] min-h-0 self-start flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 xl:sticky xl:top-3"
      aria-label={t("knowledgeBase.treeView.faqPanelTitle")}
    >
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-3 py-3 dark:border-slate-700">
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {t("knowledgeBase.treeView.faqPanelTitle")}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t("knowledgeBase.treeView.faqPanelSubtitle")}
          </div>
        </div>
        <span className="grid h-7 min-w-7 place-items-center rounded-full bg-slate-900 px-2 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
          {faqs.length}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {listStatus === "loading" && faqs.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("knowledgeBase.treeView.faqLoading")}
          </div>
        ) : orderedFaqs.length === 0 ? (
          <div className="rounded-md bg-slate-50 px-3 py-4 dark:bg-slate-800/70">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {t("knowledgeBase.treeView.faqEmptyTitle")}
            </div>
            <div className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {t("knowledgeBase.treeView.faqEmptyDescription")}
            </div>
          </div>
        ) : (
          <div className="grid gap-1">
            {orderedFaqs.map(({ faq, index }) => {
              const anchorTop =
                faqAnchors.find((anchor) => anchor.faq.id === faq.id)?.top ?? 0
              const rangeText = truncatePreviewText(getFaqRangeText(faq))
              return (
                <button
                  key={faq.id}
                  type="button"
                  className={cn(
                    "grid w-full grid-cols-[1.5rem_minmax(0,1fr)] gap-2 rounded-md border border-transparent px-3 py-2 text-left transition hover:border-teal-200 hover:bg-teal-50 dark:hover:border-teal-900 dark:hover:bg-teal-950/30",
                    activeEditor?.faq?.id === faq.id &&
                      "border-teal-200 bg-teal-50 dark:border-teal-900 dark:bg-teal-950/30"
                  )}
                  onClick={() => openEditEditor(faq, anchorTop)}
                >
                  <span className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {index}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-5 text-slate-900 dark:text-slate-100">
                      {faq.question}
                    </span>
                    {rangeText && (
                      <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {rangeText}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </aside>
  ) : null

  return (
    <>
      <div className="space-y-3">
        <style>{`::highlight(${HIGHLIGHT_NAME}) { background: rgba(45, 212, 191, 0.24); color: inherit; } ::highlight(${DRAFT_HIGHLIGHT_NAME}) { background: rgba(245, 158, 11, 0.42); color: inherit; } ::highlight(${EDIT_HIGHLIGHT_NAME}) { background: rgba(245, 158, 11, 0.42); color: inherit; }`}</style>
        <div className="not-prose flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3 text-sm dark:border-slate-700 dark:bg-slate-900/50 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="w-fit rounded-full bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700 ring-1 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-900">
              {t("knowledgeBase.treeView.faqAnnotationLabel")}
            </span>
            <span className="text-slate-600 dark:text-slate-300">
              {t("knowledgeBase.treeView.faqAnnotationHint")}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            aria-expanded={isFaqPanelOpen}
            aria-controls="knowledge-base-faq-panel"
            onClick={() => setIsFaqPanelOpen((open) => !open)}
          >
            {isFaqPanelOpen ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
            {t("knowledgeBase.treeView.faqGroupTitle", {
              count: faqs.length,
            })}
          </Button>
        </div>

        {isFaqPanelStacked && faqPanel}

        <div
          className={cn(
            "grid min-w-0 gap-3",
            isFaqPanelOpen && "xl:grid-cols-[minmax(0,1fr)_20rem]"
          )}
        >
          <div
            ref={rootRef}
            className="relative min-w-0"
            onMouseUp={handleMouseUp}
          >
            {listStatus === "loading" && faqs.length === 0 && (
              <div className="not-prose absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-xs text-slate-500 shadow-sm dark:bg-slate-900/90 dark:text-slate-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("knowledgeBase.treeView.faqLoading")}
              </div>
            )}
            <div ref={containerRef} className="min-w-0 pr-12">
              <Markdown
                components={componentProps}
                urlTransform={markdownUrlTransform}
              >
                {content}
              </Markdown>
            </div>
            {faqAnchorGroups.map((group) => {
              const firstFaq = group.anchors[0]?.faq
              const firstFaqIndex = firstFaq
                ? faqIndexById.get(firstFaq.id)
                : undefined
              return (
                <button
                  key={group.id}
                  type="button"
                  className="not-prose absolute right-2 z-40 grid h-6 min-w-6 place-items-center rounded-full border border-teal-200 bg-white/95 px-1.5 text-[11px] font-semibold leading-none text-teal-700 shadow-sm transition hover:border-teal-400 hover:bg-teal-50 hover:text-teal-900 dark:border-teal-900 dark:bg-slate-900/95 dark:text-teal-300 dark:hover:bg-teal-950/60"
                  style={{ top: Math.max(0, group.top - 5) }}
                  title={
                    group.anchors.length === 1
                      ? group.anchors[0].faq.question
                      : t("knowledgeBase.treeView.faqGroupTitle", {
                          count: group.anchors.length,
                        })
                  }
                  aria-label={t("knowledgeBase.treeView.faqEditExisting")}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onMouseUp={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation()
                    if (group.anchors.length === 1) {
                      const anchor = group.anchors[0]
                      openEditEditor(anchor.faq, anchor.top)
                      return
                    }
                    toggleAnchorGroup(group)
                  }}
                >
                  {group.anchors.length === 1
                    ? firstFaqIndex
                    : group.anchors.length}
                </button>
              )
            })}
            {activeGroup && !activeEditor && (
              <div
                className="not-prose absolute right-10 z-50 w-[min(360px,calc(100%-3rem))] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                style={{ top: Math.max(0, activeGroup.top + 28) }}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-3 py-2 dark:border-slate-700">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("knowledgeBase.treeView.faqGroupTitle", {
                        count: activeGroup.anchors.length,
                      })}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("knowledgeBase.treeView.faqGroupDescription")}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setActiveGroupId(null)}
                    aria-label={t("knowledgeBase.treeView.cancel")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-64 overflow-auto p-2">
                  {activeGroup.anchors.map(({ faq, top }) => (
                    <button
                      key={faq.id}
                      type="button"
                      className="grid w-full grid-cols-[1.5rem_minmax(0,1fr)] gap-2 rounded-md px-3 py-2 text-left text-sm leading-5 text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"
                      onClick={() => openEditEditor(faq, top)}
                    >
                      <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {faqIndexById.get(faq.id)}
                      </span>
                      <span className="font-medium">{faq.question}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {pendingSelection && !activeEditor && (
              <button
                type="button"
                className="not-prose absolute right-2 z-50 grid h-9 w-9 place-items-center rounded-full border-2 border-white bg-slate-900 text-white shadow-xl transition hover:bg-teal-700 dark:border-slate-800"
                style={{ top: Math.max(0, pendingSelection.top - 7) }}
                title={t("knowledgeBase.treeView.faqCreateFromSelection")}
                aria-label={t("knowledgeBase.treeView.faqCreateFromSelection")}
                onMouseDown={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation()
                  openCreateEditor()
                }}
              >
                <MessageSquarePlus className="h-4 w-4" />
              </button>
            )}
            {activeEditor && (
              <div
                className="not-prose absolute right-10 z-50 w-[min(390px,calc(100%-3rem))] rounded-lg border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900"
                style={{
                  top: Math.max(0, activeEditor.top + 28),
                }}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("knowledgeBase.treeView.faqTitle")}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t("knowledgeBase.treeView.faqTextSelectionDescription")}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={closeEditor}
                    aria-label={t("knowledgeBase.treeView.cancel")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {activeEditorSelectedText && (
                  <div className="mb-3 rounded-md bg-slate-50 px-3 py-2 dark:bg-slate-800/70">
                    <div className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t("knowledgeBase.treeView.faqSelectedText")}
                    </div>
                    <p className="m-0 text-xs leading-5 text-slate-700 dark:text-slate-200">
                      {activeEditorSelectedText}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    value={question}
                    maxLength={FAQ_QUESTION_MAX_LENGTH}
                    placeholder={t(
                      "knowledgeBase.treeView.faqQuestionPlaceholder"
                    )}
                    onChange={(event) => setQuestion(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return
                      event.preventDefault()
                      void saveActiveFaq()
                    }}
                    autoFocus
                  />
                  {activeEditor.mode === "edit" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                      disabled={isDeleting}
                      onClick={requestDeleteActiveFaq}
                      aria-label={t("knowledgeBase.treeView.delete")}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="icon"
                    className="h-9 w-9 bg-teal-700 text-white hover:bg-teal-800"
                    disabled={isSaving || !question.trim()}
                    onClick={() => void saveActiveFaq()}
                    aria-label={t("knowledgeBase.treeView.save")}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {!isFaqPanelStacked && faqPanel}
        </div>
      </div>

      <AlertDialog
        open={!!deleteCandidate}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteCandidate(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("knowledgeBase.treeView.faqDeleteConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("knowledgeBase.treeView.faqDeleteConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteCandidate && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-900 dark:bg-red-950/30 dark:text-red-100">
              {deleteCandidate.question}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("knowledgeBase.treeView.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault()
                void deleteActiveFaq()
              }}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("knowledgeBase.treeView.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
